import { useMemo } from 'react'

import { Chip, Icon } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import { isAssetIcon } from '@services/type-guards/Common/index'
import { FeeStatus } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { ChipProps } from '@circlefin/components/lib/Chip'
import type { FeeReport } from '@shared/graphql'

const columnHelper = createColumnHelper<FeeReport>()

export const useFeeHistoryTableColumns = () => {
  const { t } = useTranslation('settings')
  const { date } = useDate()
  const { money } = useMoney()

  return useMemo(() => {
    const startDate = columnHelper.accessor('valueDate', {
      id: 'startDate',
      size: 120,
      header: t<string>`settings:billing.feeHistory.table.headers.startDate`,
      cell: ({ getValue }) => {
        return (
          <span className="font-circular-bold">
            {date(getValue(), 'dateWithShortMonth', 'America/New_York')}
          </span>
        )
      },
    })

    const stablecoin = columnHelper.accessor('fee', {
      id: 'stablecoin',
      size: 100,
      header: t<string>`settings:billing.feeHistory.table.headers.stablecoin`,
      cell: ({ getValue }) => {
        const value = getValue()
        const icon = value.currency
        return (
          <div className="flex w-fit items-center gap-2">
            {isAssetIcon(icon) && <Icon name={icon} size={20} />}
            <span>{value.currency}</span>
          </div>
        )
      },
    })

    const redemptionAmount = columnHelper.accessor('cumulatedPayoutAmount', {
      id: 'redemptionAmount',
      header: t<string>`settings:billing.feeHistory.table.headers.redemptionAmount`,
      cell: ({ getValue }) => {
        const value = getValue()
        return money({
          number: value.amount,
          variant: value.currency,
          options: { symbol: false },
        })
      },
    })

    const mintAmount = columnHelper.accessor('cumulatedPaymentAmount', {
      id: 'mintAmount',
      header: t<string>`settings:billing.feeHistory.table.headers.mintAmount`,
      cell: ({ getValue }) => {
        const value = getValue()
        return money({
          number: value.amount,
          variant: value.currency,
          options: { symbol: false },
        })
      },
    })

    const netRedemptionAmount = columnHelper.accessor('cumulatedNetAmount', {
      id: 'netRedemptionAmount',
      size: 160,
      header: t<string>`settings:billing.feeHistory.table.headers.netRedemptionAmount`,
      cell: ({ getValue }) => {
        const value = getValue()
        return money({
          number: value.amount,
          variant: value.currency,
          options: { symbol: false },
        })
      },
    })

    const estimatedFee = columnHelper.accessor('fee', {
      id: 'estimatedFee',
      header: t<string>`settings:billing.feeHistory.table.headers.estimatedFee`,
      cell: ({ getValue }) => {
        const value = getValue()
        return (
          <span className="font-circular-bold">
            {money({
              number: value.amount,
              variant: value.currency,
              options: { symbol: false },
            })}
          </span>
        )
      },
    })

    const status = columnHelper.accessor('status', {
      id: 'status',
      size: 110,
      header: t<string>`settings:billing.feeHistory.table.headers.status`,
      cell: ({ getValue }) => {
        const value = getValue()
        const chipVariants: Record<FeeStatus, ChipProps['variant']> = {
          [FeeStatus.paid]: 'default/success',
          [FeeStatus.pending]: 'default/warning',
          [FeeStatus.scheduled]: 'default/neutral',
        }
        return (
          <Chip variant={chipVariants[value]}>
            {t(`settings:billing.feeHistory.table.statuses.${value}`)}
          </Chip>
        )
      },
    })

    return [
      startDate,
      stablecoin,
      redemptionAmount,
      mintAmount,
      netRedemptionAmount,
      estimatedFee,
      status,
    ]
  }, [date, money, t])
}
